import { supabase } from '../supabase';
import type { Property } from '../types/property';
import { cacheRentalProperties } from './rental-database-helpers';
import { cacheSoldProperties as cacheSoldPropertiesToDatabase } from './sold-database-helpers';
import { cacheProperties } from './database-helpers';
import { queuePropertiesForDatabaseBackup } from './database-backup-sync';

// Cache configuration
const MEMORY_CACHE_DURATION = 15 * 60 * 1000; // 15 minutes
const CLEANUP_INTERVAL = 5 * 60 * 1000; // 5 minutes

// Memory cache for instant results
export class PropertyCache {
  private static instance: PropertyCache;
  private cache = new Map<string, { results: Property[]; timestamp: number }>();
  private cleanupInterval: NodeJS.Timeout | null = null;

  private constructor() {
    this.startCleanupInterval();
  }

  public static getInstance(): PropertyCache {
    if (!PropertyCache.instance) {
      PropertyCache.instance = new PropertyCache();
    }
    return PropertyCache.instance;
  }

  /**
   * Get cached results from memory
   */
  public get(key: string): Property[] | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < MEMORY_CACHE_DURATION) {
      return cached.results;
    }
    
    // Remove expired entry
    if (cached) {
      this.cache.delete(key);
    }
    
    return null;
  }

  /**
   * Set cache entry in memory
   */
  public set(key: string, results: Property[]): void {
    this.cache.set(key, {
      results,
      timestamp: Date.now()
    });
  }

  /**
   * Check if cache has valid entry
   */
  public has(key: string): boolean {
    const cached = this.cache.get(key);
    return cached !== undefined && Date.now() - cached.timestamp < MEMORY_CACHE_DURATION;
  }

  /**
   * Clear specific cache entry
   */
  public clear(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Clear all cache entries
   */
  public clearAll(): void {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   */
  public getStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }

  /**
   * Start cleanup interval to prevent memory leaks
   */
  private startCleanupInterval(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }

    this.cleanupInterval = setInterval(() => {
      this.cleanup();
    }, CLEANUP_INTERVAL);
  }

  /**
   * Clean up expired cache entries
   */
  private cleanup(): void {
    const now = Date.now();
    for (const [key, value] of this.cache.entries()) {
      if (now - value.timestamp > MEMORY_CACHE_DURATION) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Stop cleanup interval (useful for testing)
   */
  public stopCleanup(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
  }
}

// Export singleton instance
export const propertyCache = PropertyCache.getInstance();

// Flag to prevent database backup sync during direct property enhancement
let isDirectPropertyEnhancement = false;

/**
 * Generate cache key for search parameters
 */
export function generateCacheKey(searchParams: any, searchType: string): string {
  const normalizedParams = { ...searchParams };
  
  // Remove any undefined or null values
  Object.keys(normalizedParams).forEach(key => {
    if (normalizedParams[key] === undefined || normalizedParams[key] === null) {
      delete normalizedParams[key];
    }
  });
  
  const cacheKey = `${searchType}-${JSON.stringify(normalizedParams)}`;
  
  return cacheKey;
}

/**
 * Cache properties to database based on their type
 * This function ensures all searches are reliably stored in the database
 */
export async function cachePropertiesToDatabase(properties: Property[], searchType: string): Promise<void> {
  if (!properties || properties.length === 0) {
    return;
  }

  try {
    
    // Deduplicate properties by source_id to prevent duplicate upserts
    const uniqueProperties = properties.filter((property, index, self) => 
      index === self.findIndex(p => p.source_id === property.source_id)
    );



    // Execute database caching immediately to ensure reliability
    let cacheResult: any;
    if (searchType === 'rent') {
      cacheResult = await cacheRentalProperties(uniqueProperties);
    } else if (searchType === 'sold') {
      cacheResult = await cacheSoldProperties(uniqueProperties);
    } else if (searchType === 'sale') {
      cacheResult = await cacheProperties(uniqueProperties);
    } else {
      cacheResult = await cacheProperties(uniqueProperties);
    }
    
  } catch (error) {
    // Attempt retry for critical database operations
    try {
      let retryResult: any;
      switch (searchType) {
        case 'rent':
          retryResult = await cacheRentalProperties(properties);
          break;
        case 'sold':
          retryResult = await cacheSoldProperties(properties);
          break;
        case 'sale':
        default:
          retryResult = await cacheProperties(properties);
          break;
      }
    } catch (retryError) {
      // Throw the error so calling code knows caching failed
      throw new Error(`Database caching failed for ${searchType} properties after retry: ${retryError instanceof Error ? retryError.message : String(retryError)}`);
    }
  }
}

/**
 * Unified search function with caching for Property arrays
 */
export async function searchWithCaching(
  searchFunction: () => Promise<Property[]>,
  cacheKey: string,
  searchType: string
): Promise<Property[]> {
  // Check memory cache first
  const cachedResults = propertyCache.get(cacheKey);
  if (cachedResults) {
    return cachedResults;
  }

  // Execute search function
  const results = await searchFunction();

  // Cache results in memory for instant access
  propertyCache.set(cacheKey, results);

  // Cache to database immediately to ensure reliability
  // This is now synchronous but with proper error handling
  try {
    await cachePropertiesToDatabase(results, searchType);
  } catch (error) {
    // Don't break the search response
    // Database caching failed but results are cached in memory
  }

  return results;
}

/**
 * Get properties from cache or database
 */
export async function getPropertiesFromCache(
  cacheKey: string,
  searchType: string,
  fallbackFunction?: () => Promise<Property[]>
): Promise<Property[]> {
  // Try memory cache first
  const cachedResults = propertyCache.get(cacheKey);
  if (cachedResults) {
    return cachedResults;
  }

  // If no fallback function, return empty array
  if (!fallbackFunction) {
    return [];
  }

  // Try to get from database or execute fallback
  try {
    const results = await fallbackFunction();
    
    // Cache the results for future use
    if (results.length > 0) {
      propertyCache.set(cacheKey, results);
    }
    
    return results;
  } catch (error) {
    return [];
  }
}

/**
 * Non-blocking database cache function for UI responsiveness
 * Use this when you want to avoid blocking the search response
 * 
 */
export function cachePropertiesToDatabaseNonBlocking(properties: Property[], searchType: string): void {
  if (!properties || properties.length === 0) {
    return;
  }

  // Skip database backup sync if we're doing direct property enhancement
  if (isDirectPropertyEnhancement) {
    return;
  }

  // ENABLED: Background database sync for backup purposes only
  // This runs completely independently and never affects local cache
  
  // Queue for background database backup (non-blocking, independent)
  queuePropertiesForDatabaseBackup(properties, searchType);
}

/**
 * NEW: Unified caching function that caches locally first, then to database
 * This ensures instant access for subsequent requests while maintaining persistence
 * 
 * @param properties - Properties to cache
 * @param searchType - Type of search (sale, rent, sold)
 * @param cacheKey - Optional specific cache key, will generate one if not provided
 * @param searchParams - Optional search parameters for cache key generation
 * @param rawUserInput - Optional raw user input for dual caching (raw + resolved)
 */
export function cachePropertiesUnified(
  properties: Property[], 
  searchType: string, 
  cacheKey?: string,
  searchParams?: any,
  rawUserInput?: string
): void {
  if (!properties || properties.length === 0) {
    return;
  }

  try {
    // STEP 1: Cache locally FIRST for instant access - LOCAL CACHE TAKES PRECEDENCE
    // This ensures that local cache data is never overwritten by database data
    // NOTE: description and daysOnMarket are kept in local cache only (not stored in DB)
    let key = cacheKey;
    if (!key) {
      // Generate a cache key based on search type and first property
      const firstProperty = properties[0];
      if (firstProperty?.source_id) {
        key = `${searchType}-property-${firstProperty.source_id}`;
      } else {
        key = `${searchType}-${Date.now()}`;
      }
    }
    
    // Cache in memory for instant access
    propertyCache.set(key, properties);
    
    // STEP 2: Database caching is disabled to prevent local cache interference
    // Local cache takes precedence - database is only used as backup/fallback
    cachePropertiesToDatabaseNonBlocking(properties, searchType);
    
    // STEP 3: Also cache by individual property ID for direct lookups
    properties.forEach(property => {
      if (property.source_id) {
        const idCacheKey = `property-id-${property.source_id}`;
        propertyCache.set(idCacheKey, [property]);
      }
    });
    
    // STEP 4: DUAL CACHING for back button navigation
    if (searchParams) {
      // Cache with resolved location (current behavior)
      const resolvedSearchCacheKey = generateCacheKey(searchParams, searchType);
      propertyCache.set(resolvedSearchCacheKey, properties);
      
      // NEW: Also cache with raw user input for instant back button access
      let rawSearchCacheKey: string | undefined;
      if (rawUserInput && rawUserInput !== searchParams.location) {
        const rawSearchParams = { ...searchParams, location: rawUserInput };
        rawSearchCacheKey = generateCacheKey(rawSearchParams, searchType);
        propertyCache.set(rawSearchCacheKey, properties);
        
        // Link both cache entries for consistency
        (properties as any).linkedCacheKeys = [resolvedSearchCacheKey, rawSearchCacheKey!];
      }
      
      // Add timestamp for cache age tracking
      (properties as any).timestamp = Date.now();
      
      // Verify the cache was set correctly
      const verifyCache = propertyCache.get(resolvedSearchCacheKey);
    }
    
  } catch (error) {
    // Fallback to just database caching if local caching fails
    cachePropertiesToDatabaseNonBlocking(properties, searchType);
  }
}

/**
 * Enhanced version of cachePropertiesToDatabaseNonBlocking that also caches locally
 * This is a drop-in replacement for existing code
 */
export function cachePropertiesToDatabaseNonBlockingWithLocal(
  properties: Property[], 
  searchType: string
): void {
  cachePropertiesUnified(properties, searchType);
}

/**
 * HIGH-LEVEL CACHING FUNCTIONS - Use these instead of cachePropertiesUnified directly
 * These handle all the complex logic internally
 */

/**
 * Cache sale properties with automatic dual caching (raw + resolved location)
 */
export function cacheSaleProperties(
  properties: Property[],
  userInput?: string,
  resolvedLocation?: string,
  additionalParams?: any
): void {
  if (!properties || properties.length === 0) return;
  
  try {
    // Create search params for caching
    const searchParams = {
      location: resolvedLocation || userInput || 'unknown',
      status: 'forSale',
      page: 0,
      sort: 'relevance',
      ...additionalParams
    };
    
    // Use unified cache with dual caching
    cachePropertiesUnified(properties, 'sale', undefined, searchParams, userInput || '');
  } catch (error) {
    // Failed to cache sale properties:
  }
}

/**
 * Cache rent properties with automatic dual caching (raw + resolved location)
 */
export function cacheRentProperties(
  properties: Property[],
  userInput?: string,
  resolvedLocation?: string,
  additionalParams?: any
): void {
  if (!properties || properties.length === 0) return;
  
  try {
    
    // Create search params for caching
    const searchParams = {
      location: resolvedLocation || userInput || 'unknown',
      status: 'forRent',
      page: 0,
      sort: 'relevance',
      ...additionalParams
    };
    
    // Use unified cache with dual caching
    cachePropertiesUnified(properties, 'rent', undefined, searchParams, userInput || '');
  } catch (error) {
    // Failed to cache rent properties
  }
}

/**
 * Cache sold properties with automatic dual caching (raw + resolved location)
 */
export function cacheSoldProperties(
  properties: Property[],
  userInput?: string,
  resolvedLocation?: string,
  additionalParams?: any
): void {
  if (!properties || properties.length === 0) return;
  
  try {
    // Create search params for caching
    const searchParams = {
      location: resolvedLocation || userInput || 'unknown',
      status: 'sold',
      page: 0,
      sort: 'relevance',
      ...additionalParams
    };
    
    // Use unified cache with dual caching
    cachePropertiesUnified(properties, 'sold', undefined, searchParams, userInput || '');
  } catch (error) {
    // Failed to cache sold properties:
  }
}

/**
 * Cache properties by search type with automatic dual caching
 */
export function cachePropertiesByType(
  properties: Property[],
  searchType: 'sale' | 'rent' | 'sold',
  userInput?: string,
  resolvedLocation?: string,
  additionalParams?: any
): void {
  if (!properties || properties.length === 0) return;
  
  try {
    // Create search params for caching
    const searchParams = {
      location: resolvedLocation || userInput || 'unknown',
      status: searchType === 'rent' ? 'forRent' : searchType === 'sold' ? 'sold' : 'forSale',
      page: 0,
      sort: 'relevance',
      ...additionalParams
    };
    
    // Use unified cache with dual caching
    cachePropertiesUnified(properties, searchType, undefined, searchParams, userInput || '');
  } catch (error) {
    // Failed to cache ${searchType} properties:
  }
}

/**
 * Batch cache multiple property sets efficiently
 * This is useful when multiple searches happen simultaneously
 */
export async function batchCachePropertiesToDatabase(
  propertyBatches: Array<{ properties: Property[]; searchType: string }>
): Promise<void> {
  if (!propertyBatches || propertyBatches.length === 0) {
    return;
  }

  try {
    // Process all batches concurrently for better performance
    const cachePromises = propertyBatches.map(async ({ properties, searchType }) => {
      try {
        await cachePropertiesToDatabase(properties, searchType);
        return { success: true, searchType, count: properties.length };
      } catch (error) {
        return { success: false, searchType, count: properties.length, error };
      }
    });

    await Promise.allSettled(cachePromises);
  } catch (error) {
    // Batch cache operation failed
  }
}

/**
 * Clear cache for specific search type
 */
export function clearCacheForType(searchType: string): void {
  const keys = propertyCache.getStats().keys;
  keys.forEach(key => {
    if (key.startsWith(searchType)) {
      propertyCache.clear(key);
    }
  });
}

/**
 * Clear cache for specific search term (e.g., "austin", "dallas")
 * This helps with repeat searches that might be hanging
 */
export function clearCacheForSearchTerm(searchTerm: string): void {
  const keys = propertyCache.getStats().keys;
  const searchTermLower = searchTerm.toLowerCase();
  
  keys.forEach(key => {
    if (key.toLowerCase().includes(searchTermLower)) {
      propertyCache.clear(key);
    }
  });
}



/**
 * Clear all caches
 */
export function clearAllCaches(): void {
  propertyCache.clearAll();
}

/**
 * Clear stale cache entries for a specific property
 * This ensures fresh data is loaded when property details are missing
 */
export function clearStalePropertyCache(propertyId: string): void {
  const cacheKeys = propertyCache.getStats().keys;
  
  // Clear individual property cache
  const idCacheKey = `property-id-${propertyId}`;
  propertyCache.clear(idCacheKey);
  
  // Clear property from all search result caches
  for (const key of cacheKeys) {
    const cachedResults = propertyCache.get(key);
    if (cachedResults) {
      const hasProperty = cachedResults.some(p => p.source_id === propertyId || p.id === propertyId);
      if (hasProperty) {
        // Remove the property from this cache entry
        const updatedResults = cachedResults.filter(p => p.source_id !== propertyId && p.id !== propertyId);
        if (updatedResults.length > 0) {
          propertyCache.set(key, updatedResults);
        } else {
          propertyCache.clear(key);
        }
      }
    }
  }
}

/**
 * Restore cache from localStorage on app startup
 * This helps maintain property details access after app restart
 */
export function restoreCacheFromLocalStorage(): void {
  try {
    const storedListings = localStorage.getItem('homeFilters_listings');
    if (storedListings) {
      const listings = JSON.parse(storedListings);
      if (Array.isArray(listings) && listings.length > 0) {
        
        // Restore listings to memory cache for instant property details access
        const cacheKey = 'restored-from-localStorage';
        propertyCache.set(cacheKey, listings);
        
        // Also cache individual properties by ID for direct lookups
        listings.forEach(property => {
          if (property.source_id) {
            const idCacheKey = `property-id-${property.source_id}`;
            propertyCache.set(idCacheKey, [property]);
          }
        });
        
      }
    }
  } catch (error) {
    // Failed to restore cache from localStorage
  }
}

/**
 * Safely update local cache with new data while preserving existing data
 * This ensures that local cache data is never lost or overwritten
 */
export function updateLocalCacheSafely(
  cacheKey: string, 
  newData: Property[], 
  mergeStrategy: 'preserve-existing' | 'replace' = 'preserve-existing'
): void {
  if (!newData || newData.length === 0) {
    return;
  }

  if (mergeStrategy === 'replace') {
    // Direct replacement
    propertyCache.set(cacheKey, newData);
    return;
  }

  // Preserve existing data strategy
  const existingData = propertyCache.get(cacheKey);
  if (!existingData) {
    // No existing data, just set the new data
    propertyCache.set(cacheKey, newData);
    return;
  }

  // Merge new data with existing data, preserving existing data
  const mergedData = newData.map(newProperty => {
    const existingProperty = existingData.find((p: Property) => 
      p.source_id === newProperty.source_id || p.id === newProperty.id
    );
    
    if (existingProperty) {
      // Merge: use existing data as base, overlay new data
      return {
        ...newProperty, // New data as base
        ...existingProperty, // Existing data takes precedence
        // Preserve existing photos and descriptions if they're more complete
        photos: existingProperty.photos && existingProperty.photos.length > 0 ? 
                existingProperty.photos : newProperty.photos,
        description: existingProperty.description && 
                    existingProperty.description !== 'Property details available' && 
                    existingProperty.description.length > 50 ? 
                    existingProperty.description : newProperty.description
      };
    }
    
    return newProperty; // New property, no merging needed
  });

  propertyCache.set(cacheKey, mergedData);
}

/**
 * Safely read from database backup without affecting local cache
 * This is used only when local cache is completely empty
 */
export async function getPropertiesFromDatabaseBackup(
  propertyId: string,
  searchType: string
): Promise<Property | null> {
  try {
    if (!supabase) {
      return null;
    }

    // Add timeout to prevent hanging
    const queryPromise = supabase
      .from(searchType === 'rent' ? 'rental_properties' : 
            searchType === 'sold' ? 'sold_properties' : 'properties')
      .select('*')
      .eq(searchType === 'rent' ? 'zpid' : 'source_id', propertyId)
      .maybeSingle();
    
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Database backup query timed out')), 5000);
    });
    
    const { data, error } = await Promise.race([queryPromise, timeoutPromise]) as any;

    if (error) {
      return null;
    }

    if (!data) {
      return null;
    }

    // Transform database data to Property interface
    // This is a read-only operation that doesn't affect local cache
    // Note: description and days_on_market are not stored in database (local cache only)
    const property: Property = {
      id: data.id || data.source_id,
      source_id: data.source_id || data.zpid,
      price: data.price,
      address: data.address,
      description: 'Property details available', // Generic fallback since description is not in DB
      photos: data.photos || [],
      details: data.details || {},
      location: data.location || { lat: data.latitude || 0, lon: data.longitude || 0 },
      daysOnZillow: data.daysOnZillow || data.days_on_zillow,
      timeOnZillow: data.timeOnZillow || data.time_on_zillow,
      // daysOnMarket is not stored in database (local cache only)
      // Add any other fields as needed
    };


    return property;

  } catch (error) {
    return null;
  }
}

/**
 * Upsert enhanced property data to database after details page loads
 * This includes description and days on market data from the details endpoint
 * 
 * NOTE: This function bypasses the database backup sync to prevent duplicate upserts
 */
export async function upsertEnhancedPropertyData(property: Property): Promise<void> {

  if (!property || !property.source_id) {
    return;
  }

  // Set flag to prevent database backup sync during direct enhancement
  isDirectPropertyEnhancement = true;

  try {

    // Determine search type based on property status
    let searchType: 'sale' | 'rent' | 'sold' = 'sale';
    if (property.status === 'forRent') {
      searchType = 'rent';
    } else if (property.status === 'sold') {
      searchType = 'sold';
    }

    // Import the appropriate upsert function and call directly (no background sync)
    switch (searchType) {
      case 'rent':
        const { upsertRentalProperty } = await import('./rental-database-helpers');
        await upsertRentalProperty(property);
        break;
      case 'sold':
        const { cacheSoldProperties } = await import('./sold-database-helpers');
        await cacheSoldProperties([property]);
        break;
      case 'sale':
      default:
        const { upsertProperty } = await import('./database-helpers');
        await upsertProperty(property);
        break;
    }


  } catch (error) {
    // Failed to upsert enhanced data to database
  } finally {
    // Reset flag to allow database backup sync again
    isDirectPropertyEnhancement = false;
  }
}

/**
 * Get cache statistics
 */
export function getCacheStats(): { size: number; keys: string[] } {
  return propertyCache.getStats();
}



/**
 * Verify database cache integrity
 * This helps ensure that properties in memory cache are also in database
 */
export async function verifyDatabaseCacheIntegrity(searchType: string): Promise<{
  memoryCount: number;
  databaseCount: number;
  missingInDatabase: string[];
}> {
  try {
    const stats = getCacheStats();
    const keys = stats.keys;
    
    // Count cache entries by type
    const memoryKeys = keys.filter(key => key.startsWith(searchType)).length;
    
    // This would need to be implemented based on your database structure
    // For now, return basic info
    return {
      memoryCount: memoryKeys,
      databaseCount: 0, // Would need database query implementation
      missingInDatabase: [] // Would need database verification
    };
  } catch (error) {
    return {
      memoryCount: 0,
      databaseCount: 0,
      missingInDatabase: []
    };
  }
}

/**
 * Force refresh of database cache for a specific search type
 * This is useful when you suspect cache inconsistencies
 */
export async function refreshDatabaseCache(searchType: string): Promise<void> {
  try {
    // Clear memory cache for this type
    clearCacheForType(searchType);
  } catch (error) {
    // Failed to refresh database cache
  }
}

/**
 * Clean up duplicate addresses in title field for Buy properties
 * This fixes the issue where both title and address columns contained the same value
 */
export async function cleanupDuplicateAddressesInTitle(): Promise<{
  success: boolean;
  message: string;
  propertiesUpdated?: number;
}> {
  try {
    return {
      success: true,
      message: 'Cleanup instructions logged. Run the SQL query to complete the cleanup.',
      propertiesUpdated: 0
    };
  } catch (error) {
    return {
      success: false,
      message: `Cleanup failed: ${error instanceof Error ? error.message : String(error)}`
    };
  }
}

/**
 * Enhance a property with API data and update all caches
 * This centralizes property enhancement logic and ensures consistent cache updates
 */
export async function enhancePropertyWithApiData(
  property: Property,
  enhancedData: {
    description?: string;
    daysOnZillow?: number | string;
    timeOnZillow?: string;
    daysOnMarket?: number | string;
    photos?: string[];
    soldDate?: string;
    sold_date?: string;
    soldPropertyMetadata?: any;
  }
): Promise<Property> {
  try {
    // Helper function to parse days values
    const parseDaysValue = (value: number | string | undefined): number | null => {
      if (typeof value === 'number') return value;
      if (typeof value === 'string') {
        const match = value.match(/\d+/);
        return match ? parseInt(match[0], 10) : null;
      }
      return null;
    };

    // Create enhanced property
    const enhancedProperty: Property = {
      ...property,
      description: enhancedData.description || property.description || '',
      // Derive daysOnMarket using any available source
      daysOnMarket:
        parseDaysValue(enhancedData.daysOnMarket) ??
        parseDaysValue(enhancedData.daysOnZillow) ??
        parseDaysValue((property as any).daysOnMarket) ??
        parseDaysValue((property as any).daysOnZillow) ??
        null,
      // Ensure photos are properly mapped
      photos: enhancedData.photos || property.photos || [],
    };

    // Add sold date information if available
    if (enhancedData.soldDate || enhancedData.sold_date) {
      (enhancedProperty as any).soldDate = enhancedData.soldDate || enhancedData.sold_date;
      (enhancedProperty as any).sold_date = enhancedData.soldDate || enhancedData.sold_date;
    }

    // Add sold property metadata if available
    if (enhancedData.soldPropertyMetadata) {
      (enhancedProperty as any).soldPropertyMetadata = {
        ...(property as any).soldPropertyMetadata,
        ...enhancedData.soldPropertyMetadata
      };
    }



    // Update memory cache if property exists in any cached search results
    const cacheKeys = propertyCache.getStats().keys;
    for (const key of cacheKeys) {
      const cachedResults = propertyCache.get(key);
      if (cachedResults) {
        const updatedResults = cachedResults.map(p => 
          p.source_id === property.source_id || p.id === property.source_id ? enhancedProperty : p
        );
        if (updatedResults.some(p => p.source_id === property.source_id)) {
          propertyCache.set(key, updatedResults);
        }
      }
    }

    // Also update individual property cache
    const idCacheKey = `property-id-${property.source_id}`;
    propertyCache.set(idCacheKey, [enhancedProperty]);

    return enhancedProperty;
  } catch (error) {
    return property;
  }
} 